<script setup lang="ts">
import { toRef } from 'vue';

import VFormSelectInput from '@modules/forms/components/inputs/VFormSelectInput/VFormSelectInput.vue';
import VFormTextInput from '@modules/forms/components/inputs/VFormTextInput/VFormTextInput.vue';
import { useUserSupervision } from '@modules/user/composables';

import type { IReportTabFooterEmits, IReportTabFooterProps } from './types';
import { useReportTabFooter } from './composables';

const props = defineProps<IReportTabFooterProps>();
const emit = defineEmits<IReportTabFooterEmits>();

const { isSupervision } = useUserSupervision();

const {
  structureStatesOptions,
  onDebouncedChangeTabName,
  inputLabelTabNameShortCode,
  isChangeTabNameLoading,
  isChangeStructureStateLoading,
  isStructureStatesLoading,
  onStructureStateChange,
} = useReportTabFooter(
  toRef(props, 'translationKey'),
  toRef(props, 'activeTab'),
  toRef(props, 'queryOptions'),
  toRef(props, 'isStructureStatesAllowed'),
  toRef(props, 'isSegmentsType'),
  emit,
);
</script>

<template>
  <div v-data-qa-auto-id="'VReportTabFooter'" class="flex flex-col sm:flex-row sm:gap-6">
    <div class="w-full p-4 sm:w-1/2 sm:min-w-[200px] sm:p-6 sm:pr-0">
      <VFormTextInput
        :value="activeTab.name || ''"
        :label="$getTranslation(translationKey, inputLabelTabNameShortCode)"
        :readonly="isChangeTabNameLoading"
        :disabled="activeTab.read_only || isSupervision"
        :icon-right="isChangeTabNameLoading ? 'pi pi-spin pi-spinner' : 'pi pi-file-edit'"
        hide-errors
        @input="onDebouncedChangeTabName"
      />
    </div>
    <div
      v-if="isStructureStatesAllowed && !activeTab.read_only"
      class="w-full p-4 sm:w-1/2 sm:min-w-[200px] sm:p-6 sm:pl-0"
    >
      <VFormSelectInput
        :value="activeTab.aasm_state"
        :options="structureStatesOptions"
        :label="$getTranslation(translationKey, 'REPORTS.STRUCTURES_TABS.aasm_state')"
        :loading="isStructureStatesLoading"
        :icon-right="isChangeStructureStateLoading ? 'pi pi-spin pi-spinner' : undefined"
        :disabled="isChangeStructureStateLoading || isSupervision"
        hide-errors
        @change="onStructureStateChange"
      />
    </div>
  </div>
</template>
